<template>
  <AppHeader>Delivery Person Attendance</AppHeader>

  <Loading v-if="state == 'loading'"></Loading>

  <div v-if="state == 'idle'" class="p-4">
    <div class="p-3 bg-white shadow-md rounded-2xl">
      <div class="px-2 py-1 mb-2 border-2 rounded-xl border-slate-100">
        <p class="text-lg text-gray-600">Delivery Person</p>
        <select class="w-full mt-2 bg-transparent outline-none" v-model="form.delivery_person_id">
          <option value="">-- Select Delivery Person --</option>
          <option v-for="deliveryPerson in deliveryPersons" :key="deliveryPerson.id" :value="deliveryPerson.id">{{ deliveryPerson.name }}</option>
        </select>
        <p class="w-full mt-1 text-sm text-left text-red-400" v-if="errors.delivery_person_id">{{ errors.delivery_person_id }}</p>
      </div>

      <div class="px-2 py-1 mb-2 border-2 rounded-xl border-slate-100">
        <p class="text-lg text-gray-600">Month</p>
        <select class="w-full mt-2 bg-transparent outline-none" v-model="form.month">
          <option value="">-- Select Month --</option>
          <option v-for="month in months" :key="month" :value="month">{{ month }}</option>
        </select>
        <p class="w-full mt-1 text-sm text-left text-red-400" v-if="errors.month">{{ errors.month }}</p>
      </div>

      <div class="mt-5 text-center">
        <button @click="filterAttendance" class="px-3 py-2 text-sm leading-none text-gray-600 uppercase rounded-full bg-slate-300">filter</button>
      </div>
    </div>

    <div v-if="trips.length == 0" class="mt-2">
      <div class="text-center text-slate-500">
        <p>Please Select Delivery Person and Month</p>
      </div>
    </div>

    <div v-else class="mt-4">
      <div class="p-1 bg-white rounded-lg shadow">
        <div class="flex justify-between m-2">
          <p class="font-bold text-gray-500">Trips</p>
          <p class="font-bold">{{ trips.summary.trips }}</p>
        </div>

        <hr>

        <p class="font-bold text-gray-500 mx-2 mt-2 mb-4 text-center">Trip Status</p>
        <div v-if="trips.summary.trips_status.created" class="flex justify-between m-2">
          <p class="font-bold text-gray-500">Created</p>
          <p class="font-bold">{{ trips.summary.trips_status.created }}</p>
        </div>

        <div v-if="trips.summary.trips_status.partial" class="flex justify-between m-2">
          <p class="font-bold text-gray-500">Partial</p>
          <p class="font-bold">{{ trips.summary.trips_status.partial }}</p>
        </div>

        <div v-if="trips.summary.trips_status.approved" class="flex justify-between m-2">
          <p class="font-bold text-gray-500">Approved</p>
          <p class="font-bold">{{ trips.summary.trips_status.approved }}</p>
        </div>

        <hr>

        <div class="flex justify-between m-2">
          <p class="font-bold text-gray-500">Customers</p>
          <p class="font-bold">{{ trips.summary.customers }}</p>
        </div>

        <div class="flex justify-between m-2">
          <p class="font-bold text-gray-500">Deliveries</p>
          <p class="font-bold">{{ trips.summary.deliveries }} ({{ trips.summary.bottles }})</p>
        </div>

        <div class="flex justify-between m-2">
          <p class="font-bold text-gray-500">Late Deliveries</p>
          <p class="font-bold">{{ trips.summary.late_deliveries }}</p>
        </div>

        <hr>

        <p class="font-bold text-gray-500 mx-2 mt-2 mb-4 text-center">Deliveries Status</p>
        <div v-for="deliveriesStatus in trips.summary.deliveries_status" :key="deliveriesStatus.status" class="flex justify-between m-2">
          <p class="font-bold text-gray-500 capitalize">{{ deliveriesStatus.status }}</p>
          <p class="font-bold">{{ deliveriesStatus.count }}</p>
        </div>
      </div>

      <div class="mt-4 responsive-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Trip Start</th>
              <th>Trip End</th>
              <th>Taken</th>
              <th>Return</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in trips.trips" :key="entry.date" :class="{'bg-red-200': entry.status == 'partial', 'bg-blue-200': entry.status == 'created', 'bg-green-200': entry.status == 'approved'}">
              <td><small>#{{ entry.id }}</small></td>
              <td>{{ toDate(entry.date) }}</td>
              <td>{{ toTime(entry.started_at, true) }}</td>
              <td>{{ toTime(entry.ended_at, true) }}</td>
              <td>
                1L: {{ entry.taken['1l'] }} |
                500ml: {{ entry.taken['500ml'] }} |
                Orders: {{ entry.taken['orders'] }}
              </td>
              <td v-if="!entry.return">Not Set</td>
              <td v-else>
                1L: {{ entry.return.full_1l }} |
                500ml: {{ entry.return.full_500ml }} |
                Orders: {{ entry.return.orders }} |
                Empty 1L: {{ entry.return.empty_1l }} |
                Empty 500ml: {{ entry.return.empty_500ml }}
              </td>
              <td><small>{{ entry.status }}</small></td>
            </tr>
          </tbody>
        </table>

        <div class="m-2" v-if="trips.trips && trips.trips.length > 0">
          <button class="px-2 py-1 mr-1 text-white rounded-full bg-slate-500" @click="downloadDpTripSummary">Excel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/common/Header.vue';
import Loading from '@/components/common/Loading.vue';
import mixin from '@/utils/mixin.js';
import formMixin from '@/utils/mixins/form';

export default {
  name: 'ReportDeliveryPersonsAttendanceSummary',
  mixins: [ mixin, formMixin ],
  components: { AppHeader, Loading },
  data() {
    return {
      state: 'loading',
      months: [],
      deliveryPersons: [],
      form: { month: '', delivery_person_id: '' },
      trips: [],
    }
  },

  methods: {
    getData() {
      this.state = 'loading';

      this.request('GET', 'reports/delivery-persons/attendance-summary', null, (resp) => {
        this.state = 'idle';
        if (resp.success) {
          this.months = resp.data.months;
          this.deliveryPersons = resp.data.delivery_persons;
        }
      })
    },

    filterAttendance() {
      let rules = {
        month: 'required',
        delivery_person_id: 'required|numeric'
      }

      if (!this.validateForm(this.form, rules));

      this.state = 'loading';

      this.request('POST', 'reports/delivery-persons/attendance-summary', this.form, (resp) => {
        this.state = 'idle';
        if (resp.success) {
          this.trips = resp.data;
        }
      })
    },

    downloadDpTripSummary() {
      this.openDownloadUrl('utils/deliveries-delivery-persons-trip-summary/' + btoa(this.$store.state.user.id + ':' + this.form.month + ':' + this.form.delivery_person_id) + '/download')
    },
  },

  created() {
    this.getData();
    if (this.$route.query.id) {
      this.form.delivery_person_id = this.$route.query.id
    }
  },
}
</script>